import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getMessages } from '../i18n';
/**
 * Initial-route loading surface for react-router v6.28 data routers.
 *
 * Wired into every `RouterProvider` call site under `apps/` via
 * `fallbackElement={<RouteFallback />}`. The data router renders ONLY the
 * `fallbackElement` while `router.state.initialized === false` — the brief
 * window between the provider mount and the first route's `lazy:` callback
 * resolving. After that point the router renders `<DataRoutes>` (or the
 * route's own suspense surface) and THIS `fallbackElement` is no longer
 * shown — so the prop is for the **initial mount only**, not for subsequent
 * lazy navigations. Omitting it (the pre-trim shape) renders `null` during
 * that initial window and the user sees an empty container
 * (web!598 product review, `554796787:b735a3060df921cd`).
 *
 * On initial mount the fallback REPLACES the route tree; it is not rendered
 * inside `<Layout/>`'s Outlet area. The Layout shell mounts around the
 * provider, but the provider renders only `fallbackElement` (or `null`)
 * while `initialized` is false, so positioning the fallback as if it were
 * inside the outlet's chrome would be incorrect.
 *
 * Constraints (from product's CHANGES REQUESTED on web!598):
 *   - ACCESSIBLE — `role="status"` + `aria-live="polite"` so assistive tech
 *     announces the loading transition once the chunk lands; the spinner
 *     carries `aria-hidden="true"` so it doesn't add duplicate noise;
 *   - LOCALIZED — copy comes from `WebUiMessages.routeFallback` (RU-first,
 *     per the brand voice lock and the languages the surface ships);
 *   - LIGHTWEIGHT — a 4×4 spinner + one line of text. No layout shift, no
 *     skeleton chrome, no brand glyph.
 *   - DETERMINISTIC test surface — `data-testid="route-fallback"` so the
 *     unresolved-lazy control (`apps/web-ui/test/route-fallback-on-lazy.test.tsx`)
 *     pins the visible state independent of timing.
 *
 * NOT a placeholder for an inline skeleton or a brand-glyph empty state — that
 * distinction belongs to the screens themselves. `RouteFallback` answers the
 * one question "the route is loading, hold on"; per-screen structure is the
 * screen's job once the chunk lands.
 */
export function RouteFallback() {
    const t = getMessages();
    return (_jsxs("div", { role: "status", "aria-live": "polite", "aria-busy": "true", "data-testid": "route-fallback", className: "flex items-center justify-center p-8 text-sm text-muted", children: [_jsx("span", { "aria-hidden": "true", className: "inline-block h-4 w-4 animate-spin rounded-full border-2 border-muted border-t-transparent" }), _jsx("span", { className: "ml-3", children: t.routeFallback })] }));
}
//# sourceMappingURL=RouteFallback.js.map